import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';

function CustomSearchableCheckboxDropdownWithLable({
  items = [],
  value,
  labelKey,
  valueKey,
  placeholder = "Select",
  onChange,
  searchable = true,
  disabled = false,
  multiple = false,
  showSelectAll = false,
  label = "",
  rootClassName = ""
}) {
  const { t } = useTranslation("common");
  
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const dropdownRef = useRef(null);

  const selectedItem =
    !multiple && items.find((item) => item[valueKey] === value);

  const selectedItems = multiple
    ? items.filter((item) => (value || []).includes(item[valueKey]))
    : [];

  const filteredItems = useMemo(() => {
    if (!searchable) return items;

    return items.filter((item) =>
      item[labelKey]
        ?.toString()
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );
  }, [items, searchText, searchable, labelKey]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchText("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    if (!multiple) {
      onChange(item[valueKey]);
      setIsOpen(false);
      return;
    }

    let newValues = [...(value || [])];

    if (newValues.includes(item[valueKey])) {
      newValues = newValues.filter((v) => v !== item[valueKey]);
    } else {
      newValues.push(item[valueKey]);
    }

    onChange(newValues);
  };

  const handleSelectAll = () => {
    if ((value || []).length === items.length) {
      onChange([]);
    } else {
      const allValues = items.map((i) => i[valueKey]);
      onChange(allValues);
    }
  };

  const selectedLabel = () => {
    if (multiple) {
      const count = selectedItems.length;

      if (count === 0) return placeholder;
      if (count === 1) return selectedItems[0][labelKey];

      return t("react_selected").replace("%s", count);
    }

    return selectedItem ? selectedItem[labelKey] : placeholder;
  };

  const allSelected = items.length > 0 && (value || []).length === items.length;

  return (
    <div
      className={`employee-list ${rootClassName}`.trim()}
      ref={dropdownRef}
    >
      <div className="dropdown form-input-container d-flex justify-content-center flex-column">

        {/* BUTTON */}
        <button
          className="dropdown-toggle position-relative custom-dropdown-btn"
          type="button"
          onClick={() => setIsOpen((s) => !s)}
          aria-expanded={isOpen}
        >
          <p className="sub-dropdown-name">{label}</p>

          <span>{selectedLabel()}</span>
        </button>

        {/* DROPDOWN */}
        <ul
          className={`dropdown-menu custom-dropdown ${isOpen ? "show" : ""}`}
        >

          {/* SEARCH */}
          {searchable && (
            <li className="px-3 py-2">
              <input
                type="text"
                className="form-control"
                placeholder={t("search")}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </li>
          )}

          {/* SELECT ALL */}
          {multiple && showSelectAll && (
            <li onClick={handleSelectAll}>
              <div className="form-check dropdown-item d-flex align-items-center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  readOnly
                  className="form-check-input me-2"
                />
                <label>{t("select_all")}</label>
              </div>
            </li>
          )}

          {/* ITEMS */}
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => {
              const checked = multiple
                ? (value || []).includes(item[valueKey])
                : value === item[valueKey];

              return (
                <li key={item[valueKey]}>
                  <button
                    type="button"
                    className="dropdown-item d-flex align-items-center"
                    onClick={() => handleSelect(item)}
                  >
                    {multiple && (
                      <div className="form-check">
                        <input
                          type="checkbox"
                          checked={checked}
                          readOnly
                          className="form-check-input me-2"
                        />
                      </div>
                    )}

                    {item[labelKey]}
                  </button>
                </li>
              );
            })
          ) : (
            <div style={{ padding: "8px 12px", color: "#9ca3af" }}>
              {t("no_zone_available")}
            </div>
          )}

        </ul>
      </div>
    </div>
  );
}

export default CustomSearchableCheckboxDropdownWithLable;
